

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import clsx from "clsx";
import * as React from "react";

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
interface TooltipContentProps extends React.ComponentPropsWithoutRef<
  typeof TooltipPrimitive.Content
> {
  arrowProps?: TooltipPrimitive.TooltipArrowProps;
}
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 8, arrowProps, children, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={`z-[999999] overflow-visible rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 ${className}`}
      {...props}
    >
      <TooltipPrimitive.Arrow
        {...arrowProps}
        className={clsx("fill-current", arrowProps?.className)}
      />
      {children}
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };

interface CustomTooltipProps {
  children: React.ReactNode;
  text: string | React.ReactNode;
  disabled?: boolean;
  className?: string;
  contentClassName?: string;
  position?: "top" | "bottom" | "left" | "right";
  isWhiteBg?: boolean;
  align?: React.ComponentPropsWithoutRef<
    typeof TooltipPrimitive.Content
  >["align"];
}

export default function CustomTooltip({
  children,
  text,
  disabled = false,
  className = "",
  contentClassName = "",
  isWhiteBg = false,
  position = "top",
  align = "center",
}: CustomTooltipProps) {
  if (disabled) {
    return (
      <span className={clsx("inline-flex items-center", className)}>
        {children}
      </span>
    );
  }

  return (
    <TooltipProvider delayDuration={200} skipDelayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild className={className}>
          <span className="inline-flex items-center">{children}</span>
        </TooltipTrigger>
        <TooltipContent
          className={`rounded-[4px] border-none ${
            isWhiteBg ? " bg-white" : " bg-black"
          } ${contentClassName}`}
          side={position}
          align={align}
          style={{ gap: 5, display: "flex", alignItems: "center" }}
          arrowProps={{
            className: isWhiteBg ? "fill-white" : "fill-black",
          }}
        >
          <p className="text-sm text-white leading-[21px] font-normal overflowAnywhere w-full ">
            {text}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
